// Hallens avbokningsvillkor, visade FÖRE köp i wizardens steg 3.
//
// Renderas i ALLA prisfall — även 0 kr. Villkoren gäller platsen oavsett vad
// bokningen kostade, och en kund som bokat gratis behöver lika mycket veta när
// tiden måste lämnas tillbaka.
//
// Raden VISAR bara. Avgiftsmotorn (FEL-006) rörs inte härifrån — den här
// komponenten anropar aldrig calculateCancellationFee, bara den delade
// tier-semantiken via buildCancellationPolicySegments.
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv as svLocale } from "date-fns/locale";
import { Info } from "lucide-react";
import {
  buildCancellationPolicySegments,
  type PolicyTier,
} from "@/lib/cancellationPolicyLine";

interface Props {
  tiers: readonly PolicyTier[] | null | undefined;
  startTime: Date;
  /** Hallens settings.cancellation.no_show.enabled — aldrig hårdkodad. */
  noShowEnabled?: boolean;
  className?: string;
}

export default function CancellationPolicyLine({
  tiers,
  startTime,
  noShowEnabled,
  className,
}: Props) {
  const { t, i18n } = useTranslation("booking");

  const segments = buildCancellationPolicySegments({ tiers: tiers ?? [], startTime });
  // Ingen tier-konfiguration → visa ingenting hellre än att gissa villkor.
  if (segments.length === 0) return null;

  const locale = i18n.language?.startsWith("sv") ? svLocale : undefined;
  const at = (d: Date) => format(d, "d MMM HH:mm", { locale });

  const free = segments.find((s) => s.percent === 0);
  const charged = segments.filter((s) => s.percent > 0);

  const parts: string[] = [];
  if (free?.until) {
    parts.push(t("policyFreeUntil", { time: at(free.until) }));
    // Nästa nivå efter den fria gränsen.
    const next = charged[0];
    if (next) parts.push(t("policyThen", { percent: next.percent }));
    // Ytterligare skärpningar får sin egen brytpunkt utskriven.
    for (const seg of charged.slice(1)) {
      if (seg.until) parts.push(t("policyFrom", { time: at(seg.until), percent: seg.percent }));
    }
  } else if (charged.length > 0) {
    // Ingen avgiftsfri nivå — börja direkt med den mildaste.
    parts.push(t("policyFrom", { time: at(startTime), percent: charged[0].percent }));
    for (const seg of charged.slice(1)) {
      if (seg.until) parts.push(t("policyFrom", { time: at(seg.until), percent: seg.percent }));
    }
  } else {
    parts.push(t("policyNoFee"));
  }

  return (
    <div
      className={className ?? "rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"}
      data-testid="cancellation-policy"
    >
      <div className="flex items-start gap-1.5">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <span className="font-medium">{t("policyHeading")}: </span>
          <span>{parts.join(", ")}.</span>
          {noShowEnabled && <span> {t("policyNoShow")}</span>}
        </div>
      </div>
    </div>
  );
}
